import { Users, Cake, Heart, Briefcase, Star, ArrowRight, Armchair, CheckCircle2, ArrowLeftRight, Ban } from 'lucide-react';
import { Button } from '@verlo/shared/components/ui/button';
import { Signal } from '@verlo/shared/components/ui/signal';
import { resolveTableState, turnStatus, metaParts, sentence } from '../../lib/fohState';
import { allergyWords } from './TableRow';
import { isJoinLead } from '../../lib/tonight';
import { duration, sinceWords } from '@verlo/shared/lib/duration';

// Out of use tonight: hatched, like a closed-off area on a plan.
export const OFF_HATCH = 'bg-[repeating-linear-gradient(135deg,transparent_0_9px,rgba(32,27,95,0.06)_9px_11px)] bg-surface-2';

const OCCASION_ICON = {
  birthday: Cake, anniversary: Heart, business: Briefcase, date_night: Heart, vip: Star, other: Star,
};

const prettyOccasion = (table) => {
  const o = table.occasion;
  if (!o || o === 'none') return null;
  if (o === 'other' && table.occasion_notes) return table.occasion_notes;
  return o.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};

/**
 * One table on the list view — the "calm row" from the walk-through.
 *
 * A 4px state rail, the number leading, one subline of context, and one action. A free
 * table is a *short* card: number, seats, one button. Everything the old card stacked (a
 * status dropdown on every card, the stage on its own line, a tags row behind a divider) is
 * gone — status changes live on the table's own screen, which is one tap away because the
 * whole card is the tap target.
 */
const TableCard = ({ table, onOpen, onMove, onLongPressStart, onLongPressEnd }) => {
  const st = resolveTableState(table);
  const status = String(table.status || '').toLowerCase();
  const occupied = status === 'occupied';
  const cleaning = status === 'cleaning';
  const turn = turnStatus(table);
  const seatedAt = occupied && table.opened_at ? sinceWords(table.opened_at) : null;

  const occasion = prettyOccasion(table);
  const OccIcon = OCCASION_ICON[table.occasion];
  const guest = table.reservation?.guest_name;

  // Since and time left are the card's clock, top right (Oct 2026 sweep, option B), so the
  // subline is just the stage and the occasion.
  const off = st.key === 'off';
  const joined = isJoinLead(table) ? 'joined tonight' : null;
  const sub = occupied ? metaParts(st.word, occasion, joined)
    : off ? metaParts(st.word, table.out_of_use?.reason)
    : metaParts(st.word, joined);
  const limit = Number(table.time_limit_minutes) || 90;

  // A cleaning table's one action is to clear it — seating happens once it's marked clean,
  // not instead of that step (that was the only way "out" of cleaning before, and it left a
  // bussed table reading "cleaning" on the floor and in the header count until it was reseated).
  const action = off
    ? { label: 'Out of use', Icon: Ban }
    : occupied
    ? { label: 'Open', Icon: ArrowRight }
    : cleaning
      ? { label: 'Mark clean', Icon: CheckCircle2 }
      : { label: 'Seat', Icon: Armchair };

  return (
    <div
      className={`group flex h-full overflow-hidden rounded-xl border border-hairline shadow-sm
                 transition-shadow hover:shadow-md cursor-pointer select-none ${off ? OFF_HATCH : 'bg-white'}`}
      onClick={() => onOpen(table)}
      onMouseDown={() => onLongPressStart?.(table)}
      onMouseUp={() => onLongPressEnd?.()}
      onMouseLeave={() => onLongPressEnd?.()}
      onTouchStart={() => onLongPressStart?.(table)}
      onTouchEnd={() => onLongPressEnd?.()}
      onContextMenu={(e) => e.preventDefault()}
      data-testid={`table-card-${table.name}`}
    >
      <div className={`w-1 shrink-0 ${st.rail}`} aria-hidden="true" />

      {/* A column that fills the card, so the action sits on the bottom edge and every card
          in the grid is the same height (the grid's rows are equal: TablesPage). */}
      <div className="flex min-w-0 flex-1 flex-col p-3.5">
        {/* Top line: number + covers/seats on the left; the clock (time left, and since
            when) top right for a seated table. The allergy goes under the number. */}
        <div className="flex items-start gap-2">
          <span className="font-mono text-[17px] font-semibold leading-none text-ink">{table.name}</span>
          <span className="font-mono text-[13px] tabular-nums text-ink-soft">
            {/* Seated with no covers taken (Chris's review): the seats, not "0 / 2". */}
            {occupied && table.covers > 0 ? `${table.covers} / ${table.capacity}` : `${table.capacity} seats`}
          </span>
          {turn && (
            <div className="ml-auto text-right leading-tight" data-testid={`table-clock-${table.name}`}>
              <div className={`font-heading text-[16px] font-extrabold tabular-nums ${turn.over ? 'text-signal-warn' : 'text-ink'}`}>
                {turn.over ? (turn.minutes >= 1 ? `${duration(turn.minutes)} over` : 'time up') : `${duration(turn.minutes)} left`}
              </div>
              {seatedAt && <div className="font-heading text-[11px] tabular-nums text-ink-faint">since {seatedAt}</div>}
            </div>
          )}
        </div>
        {allergyWords(table) && (
          <div className="mt-1.5 flex min-w-0">
            {/* Every allergen, wrapping onto a second line when it must: a till has no hover to
                read a cut-off tag. The grid keeps every card the height of the tallest. */}
            <Signal tone="stop" className="max-w-full items-start whitespace-normal leading-[1.35] [&>i]:mt-[4px]" data-testid={`table-allergy-${table.name}`}>{allergyWords(table)}</Signal>
          </div>
        )}

        {/* Subline: state word · occasion */}
        <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[13px] text-ink-soft">
          {sub.map((part, i) => (
            <span key={i} className="flex items-center gap-1.5">
              {i > 0 && <span className="h-[3px] w-[3px] rounded-full bg-ink-faint" aria-hidden="true" />}
              <span className={i === 0 ? st.text : undefined}>
                {i === 1 && OccIcon ? <OccIcon size={11} className="mr-1 inline align-[-1px]" /> : null}
                {sentence(part)}
              </span>
            </span>
          ))}
        </div>

        {/* Bottom row, on the card's bottom edge: the table's length or guest on the left, the
            one action on the right. */}
        <div className="mt-auto flex items-end justify-between gap-2 pt-2.5">
          <div className="min-w-0 space-y-1">
            {turn && !guest && (
              <div className="text-[12px] text-ink-faint">{limit} min table</div>
            )}
            {occupied && guest && (
              <div className="flex items-center gap-1.5 truncate text-[12px]">
                <Users size={12} className="shrink-0 text-ink-faint" />
                <span className="truncate font-medium text-ink-soft">{guest}</span>
                {table.reservation?.is_frequent && <span className="shrink-0 text-ink-faint">· regular</span>}
              </div>
            )}
          </div>

          <div className="flex shrink-0 gap-1.5">
          {occupied && onMove && (
            <Button
              size="sm"
              variant="outline"
              className="h-9 px-3 text-[13px] font-semibold"
              onClick={(e) => { e.stopPropagation(); onMove(table); }}
              data-testid={`table-move-${table.name}`}
            >
              <ArrowLeftRight size={14} className="mr-1.5" />
              Move
            </Button>
          )}
          <Button
            size="sm"
            variant={occupied ? 'default' : 'outline'}
            className={`h-9 shrink-0 px-3.5 text-[13px] font-semibold ${
              occupied ? 'bg-verlo hover:bg-verlo-hover' : ''
            }`}
            onClick={(e) => { e.stopPropagation(); onOpen(table); }}
            data-testid={`table-action-${table.name}`}
          >
            <action.Icon size={14} className="mr-1.5" />
            {action.label}
          </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableCard;
